import { useState } from "react";
import { Link } from "react-router-dom";
import { usePricing } from "../../context/PricingContext";
import { COUNTRIES } from "../../utils/countries";
import { countryToLang } from "../../i18n";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { X, FileText, Users, Mail, BookOpen, Shield, LayoutDashboard, Menu, ArrowRight } from "lucide-react";
import BrandMark from "../brand/BrandMark";
import WhatsAppIcon from "./WhatsAppIcon";
import { WHATSAPP_NUMBER, WHATSAPP_MESSAGE } from "./constants";
import { openWhatsApp } from "../../utils/whatsappLinks";
import { useAuth } from "../../context/AuthContext";
import { useCMSContent } from "../../hooks/useCMSContent";
import { useI18n } from "../../i18n";
import LanguageSwitcher from "../LanguageSwitcher";

const CountrySelect = ({ compact = false, onDark = false }) => {
  const { country, setCountry } = usePricing();
  const { setLangSoft, t } = useI18n();
  const found = COUNTRIES.find((c) => c.iso === country);
  const skin = onDark
    ? "bg-white/10 border-white/20 text-white hover:bg-white/20"
    : "bg-white border-slate-200 text-slate-700 hover:border-slate-300";
  return (
    <select
      value={country || "XX"}
      onChange={(e) => {
        setCountry(e.target.value);
        const l = countryToLang(e.target.value);
        if (l) setLangSoft(l); // B2: idioma sigue al país (salvo elección manual)
      }}
      className={`h-9 rounded-full border px-3 text-xs font-medium outline-none cursor-pointer transition-colors focus-visible:ring-2 focus-visible:ring-[#1B84FF] focus-visible:ring-offset-2 ${skin} ${compact ? "max-w-[86px]" : ""}`}
      aria-label={t("nav.country_aria")}
      data-testid="header-country-select"
      title={found ? found.name : t("pricing.intl_other")}
    >
      <option value="XX">🌐 {compact ? "INTL" : t("pricing.intl_other")}</option>
      {COUNTRIES.map((c) => (
        <option key={c.iso} value={c.iso}>{c.flag} {compact ? c.iso : c.name}</option>
      ))}
    </select>
  );
};

const NAV_LINKS = [
  { href: "/#como-funciona", key: "nav.how", slug: "how" },
  { href: "/#usos", key: "nav.uses", slug: "uses" },
  { href: "/#precio", key: "nav.price", slug: "price" },
  { href: "/#faq", key: "nav.faq", slug: "faq" },
];

const Navigation = () => {
  const { user, isAuthenticated } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/85 backdrop-blur-xl"
      data-testid="public-nav"
    >
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6 lg:px-8">
        <BrandMark size="sm" className="shrink-0" />

        <nav className="ml-4 hidden items-center gap-1 lg:flex" aria-label={t("nav.primary_aria")}>
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="rounded-full px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-[#0B2545] focus-visible:ring-2 focus-visible:ring-[#1B84FF] focus-visible:ring-offset-2"
            >
              {t(l.key)}
            </a>
          ))}
          <Link
            to="/nosotros"
            className="rounded-full px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-[#0B2545]"
            data-testid="nav-about-link"
          >
            {t("nav.about")}
          </Link>
          <Link
            to="/contacto"
            className="rounded-full px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-[#0B2545]"
            data-testid="nav-contact-link"
          >
            {t("nav.contact")}
          </Link>
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <div className="hidden items-center gap-2 md:flex">
            <CountrySelect compact />
            <LanguageSwitcher />
          </div>

          <Link
            to="/como-usar"
            className="hidden rounded-full border border-[#0B2545]/15 bg-[#EAF4FF] px-4 py-2 text-sm font-semibold text-[#0B2545] transition-colors hover:bg-[#D8EBFF] md:inline-flex"
            data-testid="nav-how-to-use-link"
          >
            {t("nav.install")}
          </Link>

          {isAuthenticated ? (
            <>
              <Link to="/dashboard">
                <Button
                  className="rounded-full bg-[#1B84FF] px-5 text-white transition-transform hover:-translate-y-0.5 hover:bg-[#0F6FDB]"
                  data-testid="nav-dashboard-btn"
                >
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  {t("nav.dashboard")}
                </Button>
              </Link>
              <span className="hidden text-sm font-medium text-slate-500 xl:inline">
                {user?.full_name?.split(" ")[0] || t("nav.user_fallback")}
              </span>
            </>
          ) : (
            <>
              <Link to="/login" className="hidden sm:block">
                <Button variant="ghost" className="rounded-full text-slate-600 hover:bg-slate-100 hover:text-[#0B2545]" data-testid="nav-login-btn">
                  {t("nav.login")}
                </Button>
              </Link>
              <Link to="/register">
                <Button
                  className="rounded-full bg-[#1B84FF] px-5 font-semibold text-white transition-transform hover:-translate-y-0.5 hover:bg-[#0F6FDB]"
                  data-testid="nav-start-btn"
                >
                  {t("nav.start")}
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </Link>
            </>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-[#0B2545] transition-colors hover:bg-slate-100 lg:hidden"
                aria-label={t("nav.menu_aria")}
                data-testid="nav-mobile-toggle"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86vw] max-w-sm bg-white" data-testid="nav-mobile-panel">
              <div className="mt-2 mb-8">
                <BrandMark size="sm" />
              </div>
              <div className="flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="rounded-xl px-3 py-3 text-base font-medium text-slate-700 transition-colors hover:bg-slate-100"
                    data-testid={`nav-mobile-${l.slug}`}
                  >
                    {t(l.key)}
                  </a>
                ))}
                <Link to="/nosotros" onClick={() => setOpen(false)} className="rounded-xl px-3 py-3 text-base font-medium text-slate-700 transition-colors hover:bg-slate-100">
                  {t("nav.about")}
                </Link>
                <Link to="/contacto" onClick={() => setOpen(false)} className="rounded-xl px-3 py-3 text-base font-medium text-slate-700 transition-colors hover:bg-slate-100">
                  {t("nav.contact")}
                </Link>
                <Link
                  to="/como-usar"
                  onClick={() => setOpen(false)}
                  className="mt-2 rounded-xl bg-[#EAF4FF] px-3 py-3 text-base font-semibold text-[#0B2545]"
                  data-testid="nav-how-to-use-link-mobile"
                >
                  {t("nav.install")}
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-2">
                <CountrySelect compact />
                <LanguageSwitcher />
              </div>
              <div className="mt-8 flex flex-col gap-2">
                {isAuthenticated ? (
                  <Link to="/dashboard" onClick={() => setOpen(false)}>
                    <Button className="w-full rounded-full bg-[#1B84FF] text-white hover:bg-[#0F6FDB]">
                      {t("nav.dashboard")}
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/register" onClick={() => setOpen(false)}>
                      <Button className="w-full rounded-full bg-[#1B84FF] text-white hover:bg-[#0F6FDB]">{t("nav.start")}</Button>
                    </Link>
                    <Link to="/login" onClick={() => setOpen(false)}>
                      <Button variant="outline" className="w-full rounded-full border-slate-200">{t("nav.login")}</Button>
                    </Link>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

const FloatingWhatsApp = ({ showWhatsAppOptions, setShowWhatsAppOptions }) => {
  const { t } = useI18n();
  const handleWhatsApp = () => {
    openWhatsApp(WHATSAPP_NUMBER, WHATSAPP_MESSAGE);
    setShowWhatsAppOptions(false);
  };

  return (
    <div className="fixed bottom-6 right-6 z-40" data-testid="whatsapp-floating">
      {showWhatsAppOptions && (
        <div className="absolute bottom-16 right-0 mb-2 w-72 rounded-2xl border border-slate-100 bg-white p-4 shadow-2xl">
          <div className="mb-3 flex items-center justify-between">
            <span className="font-bold text-[#0B2545]">{t("wafloat.need_help")}</span>
            <button
              onClick={() => setShowWhatsAppOptions(false)}
              className="text-slate-400 transition-colors hover:text-slate-600"
              aria-label={t("wafloat.close_aria")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="mb-4 text-sm text-slate-500">{t("wafloat.text")}</p>
          <button
            onClick={handleWhatsApp}
            className="flex w-full items-center justify-center gap-3 rounded-xl bg-[#25D366] px-4 py-3 font-medium text-white transition-colors hover:bg-[#20BD5A]"
          >
            <WhatsAppIcon className="h-5 w-5" />
            {t("wafloat.chat")}
          </button>
        </div>
      )}
      <button
        onClick={() => setShowWhatsAppOptions(!showWhatsAppOptions)}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] shadow-lg transition-transform hover:scale-110"
        aria-label={t("wafloat.chat")}
      >
        <WhatsAppIcon className="h-7 w-7 text-white" />
      </button>
    </div>
  );
};

const FooterLink = ({ to, href, children, testId }) => {
  const cls =
    "inline-flex items-center gap-2 text-sm text-slate-300 transition-colors hover:text-[#5BC0FF] focus-visible:ring-2 focus-visible:ring-[#5BC0FF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B2545]";
  if (href) return <a href={href} className={cls} data-testid={testId}>{children}</a>;
  return <Link to={to} className={cls} data-testid={testId}>{children}</Link>;
};

const Footer = () => {
  const { priceLabelSemantic, chargeNote } = usePricing();
  const { data: cms } = useCMSContent("landing_json");
  const { t } = useI18n();
  const f = cms?.footer || {};
  const handleWhatsApp = () => openWhatsApp(WHATSAPP_NUMBER, WHATSAPP_MESSAGE);
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0B2545] text-white" data-testid="footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="mb-5">
              <BrandMark size="md" onDark asLink={false} />
            </div>
            <p className="mb-5 max-w-xs text-sm leading-relaxed text-slate-300" data-testid="footer-description">
              {f.brand_description || t("footer.brand_fallback")}
            </p>
            <button
              onClick={handleWhatsApp}
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#20BD5A]"
            >
              <WhatsAppIcon className="h-4 w-4" />
              WhatsApp
            </button>
          </div>

          <div>
            <h4 className="mb-5 text-xs font-bold uppercase tracking-[0.2em] text-[#5BC0FF]">
              {f.quick_links_title || t("footer.quick_links")}
            </h4>
            <ul className="space-y-3">
              <li><FooterLink href="/#como-funciona">{t("footer.how")}</FooterLink></li>
              <li><FooterLink href="/#usos">{t("footer.uses")}</FooterLink></li>
              <li><FooterLink href="/#precio">{t("footer.prices")}</FooterLink></li>
              <li><FooterLink href="/#faq">{t("footer.faq")}</FooterLink></li>
              <li>
                <Link
                  to="/como-usar"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#5BC0FF] transition-opacity hover:opacity-80"
                  data-testid="footer-how-to-use-link"
                >
                  {t("footer.install")}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </li>
              <li><FooterLink to="/nosotros" testId="footer-about-link">{t("footer.about")}</FooterLink></li>
              <li><FooterLink to="/contacto" testId="footer-contact-link">{t("footer.contact")}</FooterLink></li>
            </ul>
          </div>

          <div>
            <h4 className="mb-5 text-xs font-bold uppercase tracking-[0.2em] text-[#5BC0FF]">
              {f.legal_title || t("footer.legal")}
            </h4>
            <ul className="space-y-3">
              <li><FooterLink to="/terminos"><FileText className="h-3.5 w-3.5" />{t("footer.terms")}</FooterLink></li>
              <li><FooterLink to="/privacidad"><Shield className="h-3.5 w-3.5" />{t("footer.privacy")}</FooterLink></li>
              <li><FooterLink to="/sobre-nosotros" testId="footer-legal-about-link"><Users className="h-3.5 w-3.5" />{t("footer.about_us")}</FooterLink></li>
              <li><FooterLink to="/reclamaciones"><BookOpen className="h-3.5 w-3.5" />{t("footer.complaints")}</FooterLink></li>
              <li><FooterLink to="/eliminar-cuenta" testId="footer-delete-account-link"><X className="h-3.5 w-3.5" />{t("footer.delete_account")}</FooterLink></li>
            </ul>
          </div>

          <div>
            <h4 className="mb-5 text-xs font-bold uppercase tracking-[0.2em] text-[#5BC0FF]">
              {f.contact_title || t("footer.contact_title")}
            </h4>
            <ul className="space-y-3">
              <li><FooterLink to="/contacto"><Mail className="h-3.5 w-3.5" />{t("footer.contact_us")}</FooterLink></li>
              <li>
                <button
                  onClick={handleWhatsApp}
                  className="inline-flex items-center gap-2 text-sm text-slate-300 transition-colors hover:text-[#5BC0FF]"
                >
                  <WhatsAppIcon className="h-3.5 w-3.5" />
                  {f.whatsapp_display || "+51 921 959 407"}
                </button>
              </li>
            </ul>

            <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-5">
              <p className="mb-1 text-xs uppercase tracking-wider text-slate-400">
                {f.membership_price_label || t("footer.membership")}
              </p>
              <p className="text-lg font-bold text-[#5BC0FF]" data-testid="footer-membership-price">
                {`${priceLabelSemantic(6) || "…"} · ${priceLabelSemantic(12) || "…"}`}
              </p>
              <p className="text-xs text-slate-400">
                {t("pricing.per_6m")} · {t("pricing.per_12m")}
              </p>
              {chargeNote(6) && (
                <p className="mt-2 text-[11px] leading-relaxed text-slate-400" data-testid="footer-charge-note">
                  {chargeNote(6)} · {chargeNote(12)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 py-6 sm:px-6 md:flex-row lg:px-8">
          <p className="text-sm text-slate-400">© {currentYear} AvisoCar. {t("footer.rights")}</p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-400">
            <LanguageSwitcher dark />
            <span>{f.made_in || t("footer.made_in")}</span>
            <span className="hidden sm:inline">·</span>
            <span>{f.company_line || "Sharan Enterprise Latam E.I.R.L."}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export { Navigation, FloatingWhatsApp, Footer };
